import { useState, useEffect } from 'react';
import { 
  X, ChevronLeft, ChevronRight, SkipForward, Check, 
  Sparkles, Phone, Users, BarChart3, Shield, BookOpen,
  Brain, Target, Trophy, FileText, Settings, Bell,
  Play, Lightbulb, Rocket, Star, Zap, ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';

// Enhanced Tour steps for Admin Panel
export const ADMIN_TOUR_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to Insyra AI!',
    subtitle: 'Your AI-Powered Call Quality Platform',
    description: 'Transform your recruitment calls with intelligent analysis, real-time coaching, and comprehensive quality monitoring.',
    icon: Rocket,
    iconColor: 'text-blue-500',
    bgGradient: 'from-blue-500/10 to-purple-500/10',
    highlight: null,
    tips: [
      'AI analyzes every call automatically',
      'Get instant quality scores & feedback',
      'Track team performance in real-time'
    ]
  },
  {
    id: 'dashboard',
    title: 'Dashboard Overview',
    subtitle: 'Your Command Center',
    description: 'View total calls, average scores, active users, and critical alerts at a glance. Use filters to analyze trends over time.',
    icon: BarChart3,
    iconColor: 'text-emerald-500',
    bgGradient: 'from-emerald-500/10 to-teal-500/10',
    highlight: 'dashboard',
    tips: [
      'Click on stats cards for detailed breakdowns',
      'Use date filters to compare performance periods',
      'Watch for red alerts requiring immediate action'
    ],
    quickAction: { label: 'View Dashboard', path: '/dashboard' }
  },
  {
    id: 'calls',
    title: 'Calls & Analysis',
    subtitle: 'Deep Dive into Every Conversation',
    description: 'Review calls with AI transcripts, quality scores across 10+ parameters, and actionable feedback. Each call is scored automatically.',
    icon: Phone,
    iconColor: 'text-blue-500',
    bgGradient: 'from-blue-500/10 to-cyan-500/10',
    highlight: 'calls',
    tips: [
      'Click any call to see full transcript & scores',
      'Filter by score range to find improvement areas',
      'Listen to audio recordings directly in-app'
    ],
    features: ['AI Transcription', 'Quality Scoring', 'Audio Playback']
  },
  {
    id: 'users',
    title: 'Team Management',
    subtitle: 'Build & Monitor Your Team',
    description: 'Create team members, assign roles and departments, set up reporting hierarchies, and track individual performance metrics.',
    icon: Users,
    iconColor: 'text-purple-500',
    bgGradient: 'from-purple-500/10 to-pink-500/10',
    highlight: 'users',
    tips: [
      'Assign users to auditors for targeted QA',
      'Set passwords for login access',
      'View individual user performance trends'
    ]
  },
  {
    id: 'alerts',
    title: 'Alerts & Compliance',
    subtitle: 'Stay Compliant, Stay Protected',
    description: 'Monitor compliance alerts, EEOC violations, and policy adherence. Critical issues are flagged automatically for immediate review.',
    icon: Shield,
    iconColor: 'text-red-500',
    bgGradient: 'from-red-500/10 to-orange-500/10',
    highlight: 'alerts',
    tips: [
      'Review critical alerts daily',
      'Use QA Review to document your actions',
      'Override compliance status when needed'
    ],
    features: ['Critical Alerts', 'QA Review', 'Compliance Override']
  },
  {
    id: 'scoring',
    title: 'Scoring Guide',
    subtitle: 'Understand How Quality is Measured',
    description: 'Review the comprehensive scoring criteria, parameter weights, and classification thresholds. This is your reference for quality standards.',
    icon: Target,
    iconColor: 'text-amber-500',
    bgGradient: 'from-amber-500/10 to-yellow-500/10',
    highlight: 'scoring-guide',
    tips: [
      'Focus on high-weight parameters first',
      'Avoid fatal parameters at all costs',
      'Share this guide with your team'
    ]
  },
  {
    id: 'coaching',
    title: 'AI Communication Coach',
    subtitle: 'Personalized Training for Everyone',
    description: 'Get AI-powered coaching based on call analysis. Practice scenarios, take assessments, and receive personalized improvement plans.',
    icon: Brain,
    iconColor: 'text-indigo-500',
    bgGradient: 'from-indigo-500/10 to-violet-500/10',
    highlight: 'ai-coach',
    tips: [
      'Assign mock training to struggling team members',
      'Review coaching insights weekly',
      'Track improvement over time'
    ],
    features: ['AI Insights', 'Mock Training', 'Practice Sessions']
  },
  {
    id: 'leaderboard',
    title: 'Leaderboard & Gamification',
    subtitle: 'Motivate Through Competition',
    description: 'Track team rankings across categories. Celebrate top performers and identify those who need extra support.',
    icon: Trophy,
    iconColor: 'text-yellow-500',
    bgGradient: 'from-yellow-500/10 to-amber-500/10',
    highlight: 'leaderboard',
    tips: [
      'Share weekly winners with the team',
      'Use rankings to identify training needs',
      'Gamification improves engagement'
    ]
  },
  {
    id: 'settings',
    title: 'Settings & Configuration',
    subtitle: 'Customize Your Experience',
    description: 'Configure API integrations, manage LLM settings, set up notifications, and customize your workspace.',
    icon: Settings,
    iconColor: 'text-gray-500',
    bgGradient: 'from-gray-500/10 to-slate-500/10',
    highlight: 'settings',
    tips: [
      'Set up LemonFox API for transcription',
      'Configure notification preferences',
      'Review system usage periodically'
    ]
  },
  {
    id: 'complete',
    title: "You're Ready!",
    subtitle: 'Start Improving Call Quality Today',
    description: 'Explore your dashboard, review some calls, and start using AI coaching. You can restart this tour anytime from the Help menu.',
    icon: Sparkles,
    iconColor: 'text-blue-500',
    bgGradient: 'from-blue-500/10 to-purple-500/10',
    highlight: null,
    tips: [
      'Review your first batch of calls',
      'Set up user assignments',
      'Check the Scoring Guide for standards'
    ],
    isComplete: true
  }
];

// Enhanced Tour steps for User Profile
export const USER_TOUR_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to Your Profile!',
    subtitle: 'Your Personal Quality Dashboard',
    description: 'Track your performance, review feedback, and get personalized coaching to become a top performer.',
    icon: Rocket,
    iconColor: 'text-blue-500',
    bgGradient: 'from-blue-500/10 to-purple-500/10',
    highlight: null,
    tips: [
      'Every call is analyzed automatically',
      'Get instant feedback & coaching',
      'Compete with peers on leaderboards'
    ]
  },
  {
    id: 'dashboard',
    title: 'Your Dashboard',
    subtitle: 'Performance at a Glance',
    description: 'See your average score, total calls, best performances, and current ranking. Track your improvement over time.',
    icon: BarChart3,
    iconColor: 'text-emerald-500',
    bgGradient: 'from-emerald-500/10 to-teal-500/10',
    highlight: 'dashboard',
    tips: [
      'Aim for the "Best" classification (≥8.5)',
      'Check your weekly trends',
      'Compare with your past performance'
    ]
  },
  {
    id: 'calls',
    title: 'My Calls',
    subtitle: 'Review & Learn from Every Call',
    description: 'Access all your calls with AI transcripts, detailed scores, and specific feedback. Listen to recordings to self-improve.',
    icon: Phone,
    iconColor: 'text-blue-500',
    bgGradient: 'from-blue-500/10 to-cyan-500/10',
    highlight: 'calls',
    tips: [
      'Review low-scoring calls for learning',
      'Read the AI feedback carefully',
      'Listen to your best calls for inspiration'
    ],
    features: ['Transcripts', 'Scores', 'Audio']
  },
  {
    id: 'coaching',
    title: 'AI Communication Coach',
    subtitle: 'Your Personal Training Partner',
    description: 'Get AI-powered coaching based on your actual calls. Practice scenarios, take assessments, and improve your skills.',
    icon: Brain,
    iconColor: 'text-indigo-500',
    bgGradient: 'from-indigo-500/10 to-violet-500/10',
    highlight: 'ai-coach',
    tips: [
      'Complete assigned mock training',
      'Practice with voice interactions',
      'Review coaching tips after each session'
    ],
    features: ['AI Coach', 'Mock Training', 'Voice Practice']
  },
  {
    id: 'scoring',
    title: 'Scoring Guide',
    subtitle: 'Know What Excellence Looks Like',
    description: 'Understand exactly how your calls are scored. Review parameters, weights, and tips to maximize your performance.',
    icon: Target,
    iconColor: 'text-amber-500',
    bgGradient: 'from-amber-500/10 to-yellow-500/10',
    highlight: 'scoring-guide',
    tips: [
      'High-weight parameters impact score most',
      'Avoid fatal parameters completely',
      'Use tips to improve each area'
    ]
  },
  {
    id: 'leaderboard',
    title: 'Leaderboard',
    subtitle: 'See How You Rank',
    description: 'Compare your performance with peers across multiple categories. Your position is highlighted for easy tracking.',
    icon: Trophy,
    iconColor: 'text-yellow-500',
    bgGradient: 'from-yellow-500/10 to-amber-500/10',
    highlight: 'leaderboard',
    tips: [
      'Aim for top 10 in your category',
      'Check different ranking categories',
      'Celebrate your improvements!'
    ]
  },
  {
    id: 'repository',
    title: 'Document Repository',
    subtitle: 'Resources at Your Fingertips',
    description: 'Access quality guidelines, training materials, compliance documents, and best practices. Preview files directly in-app.',
    icon: FileText,
    iconColor: 'text-green-500',
    bgGradient: 'from-green-500/10 to-emerald-500/10',
    highlight: 'repository',
    tips: [
      'Review guidelines regularly',
      'Download materials for offline study',
      'Check for new uploads weekly'
    ]
  },
  {
    id: 'complete',
    title: "Ready to Excel!",
    subtitle: 'Your Journey to Excellence Begins',
    description: 'Start by reviewing your recent calls, check your coaching recommendations, and aim for that top spot on the leaderboard!',
    icon: Sparkles,
    iconColor: 'text-blue-500',
    bgGradient: 'from-blue-500/10 to-purple-500/10',
    highlight: null,
    tips: [
      'Review your last 5 calls today',
      'Complete any pending training',
      'Set a personal improvement goal'
    ],
    isComplete: true
  }
];

export default function AppTour({ steps, onComplete, isAdmin = false }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);

  const step = steps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;
  const progress = ((currentStep + 1) / steps.length) * 100;
  
  const IconComponent = step.icon;

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(prev => prev + 1);
        setIsAnimating(false);
      }, 150);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(prev => prev - 1);
        setIsAnimating(false);
      }, 150);
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    setIsVisible(false);
    // Store that tour was completed
    localStorage.setItem(isAdmin ? 'admin_tour_completed' : 'user_tour_completed', 'true');
    setTimeout(() => {
      onComplete?.();
    }, 300);
  };

  const handleGoToStep = (idx) => {
    if (idx !== currentStep) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(idx);
        setIsAnimating(false);
      }, 150);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-300">
      <div className={`bg-card rounded-2xl shadow-2xl max-w-xl w-full overflow-hidden border transform transition-all duration-300 ${isAnimating ? 'scale-95 opacity-50' : 'scale-100 opacity-100'}`}>
        {/* Progress Bar */}
        <div className="h-1.5 bg-muted">
          <div 
            className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-xs font-medium px-2.5 py-1">
                {currentStep + 1} of {steps.length}
              </Badge>
              {step.isComplete && (
                <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                  <Check className="h-3 w-3 mr-1" />
                  Complete
                </Badge>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSkip}
              className="text-muted-foreground hover:text-foreground -mr-2"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Icon & Gradient Background */}
          <div className={`relative rounded-xl p-6 mb-6 bg-gradient-to-br ${step.bgGradient}`}>
            <div className="flex items-center justify-center">
              <div className={`p-4 rounded-2xl bg-white dark:bg-gray-800 shadow-lg ${step.iconColor}`}>
                <IconComponent className="h-10 w-10" />
              </div>
            </div>
            {/* Decorative elements */}
            <div className="absolute top-2 right-2 opacity-20">
              <Sparkles className="h-6 w-6" />
            </div>
            <div className="absolute bottom-2 left-2 opacity-20">
              <Star className="h-5 w-5" />
            </div>
          </div>

          {/* Title & Subtitle */}
          <div className="text-center mb-4">
            <h3 className="text-2xl font-bold text-foreground mb-1">{step.title}</h3>
            {step.subtitle && (
              <p className="text-sm font-medium text-muted-foreground">{step.subtitle}</p>
            )}
          </div>

          {/* Description */}
          <p className="text-center text-muted-foreground leading-relaxed mb-6">
            {step.description}
          </p>

          {/* Feature Badges */}
          {step.features && (
            <div className="flex items-center justify-center gap-2 mb-6 flex-wrap">
              {step.features.map((feature, idx) => (
                <Badge key={idx} variant="outline" className="text-xs">
                  <Zap className="h-3 w-3 mr-1 text-amber-500" />
                  {feature}
                </Badge>
              ))}
            </div>
          )}

          {/* Tips Section */}
          {step.tips && step.tips.length > 0 && (
            <Card className="mb-6 border-dashed">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Lightbulb className="h-4 w-4 text-amber-500" />
                  <span className="text-sm font-semibold">Quick Tips</span>
                </div>
                <ul className="space-y-2">
                  {step.tips.map((tip, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <ArrowRight className="h-3.5 w-3.5 mt-0.5 text-blue-500 flex-shrink-0" />
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <div>
              {!isFirstStep && (
                <Button variant="ghost" onClick={handlePrev} className="gap-1">
                  <ChevronLeft className="h-4 w-4" />
                  Back
                </Button>
              )}
            </div>

            <div className="flex items-center gap-2">
              {!isLastStep && (
                <Button variant="ghost" onClick={handleSkip} className="text-muted-foreground text-sm">
                  Skip Tour
                </Button>
              )}
              
              <Button 
                onClick={handleNext} 
                className={`gap-1 ${step.isComplete ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
              >
                {isLastStep ? (
                  <>
                    <Check className="h-4 w-4" />
                    Start Exploring
                  </>
                ) : (
                  <>
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Step Indicators - Clickable */}
        <div className="px-6 pb-5 pt-2 border-t bg-muted/30">
          <div className="flex justify-center gap-2">
            {steps.map((s, idx) => (
              <button
                key={idx}
                onClick={() => handleGoToStep(idx)}
                className={`w-2.5 h-2.5 rounded-full transition-all duration-300 hover:scale-125 ${
                  idx === currentStep 
                    ? 'bg-blue-500 w-6' 
                    : idx < currentStep 
                    ? 'bg-blue-300 dark:bg-blue-700' 
                    : 'bg-gray-300 dark:bg-gray-600'
                }`}
                title={s.title}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
